<template>
  <DatatableReportV3
    v-if="config"
    title="Opportunities"
    :source="source"
    :config="config"
    :filters="filters"
  />
</template>

<script>
import { mapActions, mapState } from "vuex";

import CONSTANTS from "@/Constants.js";
import JobLeadLink from "@/Job/JobLeadLink.vue";
import JobOpportunityLink from "@/Job/Opportunity/JobOpportunityLink.vue";
import MembershipOpportunityLink from "@/Job/Opportunity/MembershipOpportunityLink.vue";
import OpportunityActionsMenu from "@/Job/Opportunity/OpportunityActionsMenu.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import TagList from "@/Tags/TagList.vue";
import store from "@/vuex/store";

export default {
  components: {
    DatatableReportV3
  },
  props: {
    source: {
      type: String,
      default: ""
    },
    filters: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      config: null
    };
  },
  computed: {
    ...mapState(["tenant", "fetchingTenant"])
  },
  watch: {
    tenant(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setConfig();
      }
    }
  },
  mounted() {
    console.log(this.filters);
    if (!this.tenant && !this.fetchingTenant) {
      this.fetchTenant();
    }
    if (this.tenant) {
      this.setConfig();
    }
    this.$store.dispatch("addBreadcrumb", { label: "Opportunities" });
  },
  methods: {
    ...mapActions(["fetchTenant"]),
    setConfig() {
      this.config = {
        order: [[0, "desc"]],
        columns: this.getColumns(),
        vueComponents: [
          {
            rootId: "job-opportunity-modal-vue",
            component: JobOpportunityLink,
            options: {
              store
            }
          },
          {
            rootId: "job-lead-link-vue",
            component: JobLeadLink,
            options: {
              store
            }
          },
          {
            rootId: "opportunity-action-menu-vue",
            component: OpportunityActionsMenu,
            options: { store }
          },
          {
            rootId: "tag-list-vue",
            component: TagList
          },
          {
            rootId: "membership-opportunity-link-vue",
            component: MembershipOpportunityLink,
            options: {
              store
            }
          }
        ]
      };
    },
    getColumns() {
      const { call_rail_enabled } = this.tenant;
      if (call_rail_enabled) {
        return [
          {
            data: "date",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.days,
            filterName: "opportunity_date",
            searchable: false
          },
          {
            data: "job",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.jobs
          },
          {
            data: "tags",
            title: "Job Tags",
            orderable: false
          },
          {
            data: "customer",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.customers
          },
          { data: "customer_email", title: "Email" },
          { data: "customer_phone", title: "Phone" },
          { data: "location_name", title: "Location Name" },
          {
            data: "address",
            orderable: false,
            searchable: true
          },
          {
            data: "department",
            filterName: "department_id",
            searchable: false
          },
          {
            data: "technician",
            title: "Owner",
            filterName: "technician_id"
          },
          {
            data: "opportunity_status",
            title: "Status",
            orderable: false,
            searchable: false
          },
          {
            data: "lead_type",
            title: "Lead Type",
            filterName: "lead_type"
          },
          {
            data: "lead_source",
            title: "Lead Source",
            filterName: "lead_source",
            orderable: false
          },
          {
            data: "call_claimed_by",
            title: "Call Assigned To",
            filterName: "call_claimed_by",
            searchable: false
          },
          {
            data: "booking_call_source",
            title: "Booking Call Source",
            filterName: "call_source",
            orderable: false,
            searchable: false
          },
          {
            data: "membership_opportunity",
            title:
              "MO? <i class='fa fa-info-circle' data-tippy-content='Membership Opportunity?'></i>",
            className: "text-center",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.membership_opportunity,
            searchable: false,
            footerClassName: "text-center"
          },
          {
            data: "membership_sold",
            title:
              "MS? <i class='fa fa-info-circle' data-tippy-content='Membership Sold?'></i>",
            orderable: false,
            className: "text-center",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.membership_sold,
            searchable: false,
            footerClassName: "text-center"
          },
          {
            data: "revenue",
            title: "Revenue",
            className: "text-right",
            searchable: false,
            footerClassName: "text-right"
          },
          {
            data: "actions",
            title: "",
            orderable: false,
            searchable: false
          }
        ];
      }
      return [
        {
          data: "date",
          aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.days,
          filterName: "opportunity_date"
        },
        {
          data: "job",
          aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.jobs
        },
        { data: "tags", title: "Job Tags", orderable: false },
        {
          data: "customer",
          aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.customers
        },
        {
          data: "address",
          orderable: false,
          searchable: true
        },
        {
          data: "department",
          filterName: "department_id"
        },
        {
          data: "technician",
          title: "Owner",
          filterName: "technician_id"
        },
        {
          data: "opportunity_status",
          title: "Status",
          orderable: false
        },
        {
          data: "lead_type",
          title: "Lead Type",
          filterName: "lead_type"
        },
        {
          data: "lead_source",
          title: "Lead Source",
          filterName: "lead_source",
          orderable: false
        },
        {
          data: "membership_opportunity",
          title:
            "MO? <i class='fa fa-info-circle' data-tippy-content='Membership Opportunity?'></i>",
          className: "text-center",
          aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.membership_opportunity
        },
        {
          data: "membership_sold",
          title:
            "MS? <i class='fa fa-info-circle' data-tippy-content='Membership Sold?'></i>",
          orderable: false,
          className: "text-center",
          aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.membership_sold,
          searchable: false
        },
        {
          data: "revenue",
          title: "Revenue",
          className: "text-right",
          searchable: false
        },
        {
          data: "actions",
          title: "",
          orderable: false,
          searchable: false
        }
      ];
    }
  }
};
</script>

<style lang="scss" scoped></style>
